import React, { createContext, useContext, useEffect, useMemo } from "react";
import { content } from "./content";

const LanguageContext = createContext(null);

/**
 * Route-driven language provider.
 *
 * The current language is received as a prop from the route (/ = en, /ar = ar).
 * There is intentionally no state, no toggle, and no localStorage — the URL IS
 * the single source of truth. To switch language, the UI navigates to the
 * matching route (full page load), which gives us a clean, mobile-stable
 * remount with zero in-place reconciliation.
 */
export function LanguageProvider({ children, lang }) {
    const dir = lang === "ar" ? "rtl" : "ltr";
    const isRTL = lang === "ar";
    // The URL of the OPPOSITE language — used by the language switcher.
    const otherHref = lang === "ar" ? "/" : "/ar";
    const otherLang = lang === "ar" ? "en" : "ar";

    // Keep <html lang/dir>, canonical, and the document title in sync per route,
    // so SEO and screen readers see the right metadata without a hard reload.
    useEffect(() => {
        if (typeof document === "undefined") return;
        document.documentElement.lang = lang;
        document.documentElement.dir = dir;

        // Use the actual origin so canonical/og URLs match whichever domain is
        // serving the site (preview, staging, or the production custom domain).
        const origin =
            typeof window !== "undefined" && window.location?.origin
                ? window.location.origin
                : "";
        const pathForLang = lang === "ar" ? "/ar" : "/";

        // Dynamic canonical per route
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
            canonical.setAttribute("href", `${origin}${pathForLang}`);
        }

        // Language-appropriate title
        document.title =
            lang === "ar"
                ? "جامعة الذكاء الاصطناعي السورية (SAU) — مبادرة سورية لتعليم الذكاء الاصطناعي العملي | انطلاق قريبًا"
                : "Syrian AI University (SAU) — A Syrian Initiative for Practical AI Education | Launching Soon";

        // Language-appropriate meta description (keeps Google-friendly snippet in sync)
        const descMeta = document.querySelector('meta[name="description"]');
        if (descMeta) {
            descMeta.setAttribute(
                "content",
                lang === "ar"
                    ? "جامعة الذكاء الاصطناعي السورية (SAU) هي مبادرة تعليمية سورية قادمة تهدف إلى جعل تعلم الذكاء الاصطناعي عمليًا ومُتاحًا وجاهزًا للمستقبل — من خلال ورش عمل وموارد تعليمية منتقاة. انطلاق قريبًا."
                    : "Syrian AI University (SAU) is an upcoming Syrian educational initiative to make artificial intelligence learning practical, accessible, and future-ready — through workshops, curated resources, and real-world applications. Launching soon."
            );
        }

        // Open Graph locale + url
        const ogLocale = document.querySelector('meta[property="og:locale"]');
        if (ogLocale) ogLocale.setAttribute("content", lang === "ar" ? "ar_SY" : "en_US");
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) ogUrl.setAttribute("content", `${origin}${pathForLang}`);
    }, [lang, dir]);

    const value = useMemo(
        () => ({
            lang,
            dir,
            isRTL,
            t: content[lang],
            otherHref,
            otherLang,
        }),
        [lang, dir, isRTL, otherHref, otherLang]
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useLang must be used within LanguageProvider");
    return ctx;
}
